<template>
  <div v-loading="isLoading">
    <div v-if="showTopBar" class="workflow-top-bar">
      <div v-if="showLiveInfo" class="live-info">
        <span class="live-badge">{{ $t('CampaignForm_Workflow_Live') }}</span>
        <span class="last-edited">
          <strong>{{
            $t('CampaignForm_Workflow_LastPublishedByPrefix')
          }}</strong>
          <gravatar
            :email="workflowMeta.lastModifiedBy || workflowMeta.createdBy"
            :size="20"
            class="last-edited-avatar"
          />
          <strong class="last-edited-name">{{ lastEditedName }}</strong>
          <span>{{ $t('CampaignForm_Workflow_LastPublishedByOn') }}</span>
          <span>{{
            formatDate(
              workflowMeta.publishedAt
                ? workflowMeta.publishedAt
                : workflowMeta.updatedAt
            )
          }}</span>
        </span>
      </div>
      <div class="workflow-top-actions">
        <el-button
          v-if="showCopyFromButton"
          type="text"
          class="copy-from-text-btn"
          icon="el-icon-copy-document"
          @click="openCopyModal"
        >
          {{ $t('CampaignForm_Button_CopyFrom') }}
        </el-button>
        <el-button
          v-if="showLiveInfo && $can('updateProjectItem')"
          type="primary"
          plain
          :disabled="isDisabled"
          @click="handleEditDraft"
        >
          <i class="el-icon-edit-outline" />
          {{ editDraftJourneyText }}
        </el-button>
        <el-button
          v-if="shouldShowDraftEditTopRight"
          type="primary"
          plain
          :disabled="isDisabled || !!model.mxa_migration"
          @click="handleEdit"
        >
          <i class="el-icon-edit-outline" />
          {{ $t('CampaignForm_Workflow_EditWorkflow') }}
        </el-button>
      </div>
    </div>

    <el-dialog
      :visible="modalVisible"
      width="632px"
      :before-close="closeModal"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CampaignForm_CopyFromDialog_Title') }}
        </div>
      </div>
      <div class="items-container" v-loading="modalIsLoading">
        <radio-group v-model="selectedVariant">
          <el-table
            :data="workflowsAvailableForCopy"
            :stripe="true"
            class="table-container outlined"
            :max-height="500"
          >
            <el-table-column :label="$t('CampaignForm_CopyFromDialog_Column')">
              <template v-slot="scope">
                <radio :label="scope.row" :title="scope.row.title" />
              </template>
            </el-table-column>
          </el-table>
        </radio-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          @click="applyCopy"
          :disabled="selectedVariant === null"
        >
          <span>{{ $t('Campaign_Dialog_Button_Apply') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>
    <el-row
      v-if="hasCopyFromVariants && !showCopyFromButton"
      class="Workflow--Actions"
      type="flex"
      justify="end"
    >
      <el-button
        type="primary"
        icon="el-icon-copy-document"
        @click="openCopyModal"
      >
        {{ $t('CampaignForm_Button_CopyFrom') }}
      </el-button>
    </el-row>
    <iframe
      v-loading="workflowLoading"
      v-if="workflowId"
      :src="url"
      allow="clipboard-read; clipboard-write"
    />
    <el-row class="footer">
      <el-col class="footer-ct btn-ct">
        <div
          :class="[
            isFramed
              ? 'nav-framed'
              : isCollapsed
              ? 'nav-collapsed'
              : 'nav-expanded'
          ]"
        >
          <el-button
            v-if="shouldShowEditButton"
            type="primary"
            plain
            @click="handleEdit"
            :disabled="isDisabled || !!model.mxa_migration"
          >
            <i class="el-icon-edit-outline"></i>
            {{ $t('CampaignForm_Workflow_EditWorkflow') }}
          </el-button>
        </div>
        <el-button type="primary" @click="nextStep" data-qa="next-step-btn">
          {{ nextBtnLabel }}
          <i class="el-icon-arrow-right el-icon-right"></i>
        </el-button>
      </el-col>
    </el-row>
    <workflow-builder
      v-if="showBuilder"
      :project-id="projectId"
      :mxa-domain="mxaDomain"
      :workflow-id="workflowId"
      :on-close="onClose"
      :new-mxa="!!model.new_mxa"
      :is-draft="isDraft"
    />
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import { customConfirm, projectEndpoint } from '@/core/helpers'
import WorkflowBuilder from '@/common/components/workflow/WorkflowBuilder'
import Gravatar from '@/common/components/Gravatar'
import { campaignStatus } from '@/modules/campaign-manage/enum/CampaignStatusEnum'
import { campaignStatusCanonical } from '@/modules/campaign-manage/enum/CampaignStatusCanonicalEnum'
import { EventBus } from '@/core/EventBus'
import { $can, $hasFeature } from '@/core/utils/VueAcl'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { cloneDeep } from 'lodash'

export default {
  name: 'Workflow',

  components: {
    WorkflowBuilder,
    RadioGroup,
    Radio,
    Gravatar
  },

  props: {
    value: {
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    title: {
      required: true
    },
    variant: {
      required: false
    }
  },

  data() {
    return {
      mxaDomain: this.$store.getters.mxaDomain,
      projectId: this.$store.getters.projectId,
      workflowId: this.value.workflow_id,
      showBuilder: false,
      isDraft: false,
      isDisabled: false,
      campaignStatus,
      isCollapsed: window.localStorage.getItem('navState') === 'collapsed',
      modalVisible: false,
      selectedVariant: null,
      isLoading: false,
      modalIsLoading: false,
      workflowsAvailableForCopy: [],
      workflowLoading: false,
      hasDraft: false,
      workflowMeta: null
    }
  },

  created() {
    if (!this.value.workflow_id) {
      if (this.readonly) {
        return this.$message({
          message: this.$t('CampaignForm_Workflow_NotCreated'),
          type: 'warning'
        })
      }
      this.workflowLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'campaigns/create-workflow',
          data: {
            campaignId: this.model.id,
            features: {
              voucher_codes: !!this.model.voucher_codes_enabled,
              new_mxa: !!this.model.new_mxa
            }
          }
        })
      )
        .then(({ data }) => {
          this.value.workflow_id = data.id
          this.workflowId = data.id
          this.workflowLoading = false
        })
        .catch(error => {
          this.workflowLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    } else {
      if (
        (this.hasVoucherCodes ||
          this.model.status_canonical === campaignStatusCanonical.DRAFT ||
          this.model.status === campaignStatus.PAUSED) &&
        !this.readonly
      ) {
        this.workflowLoading = true
        RestApi(
          projectEndpoint({
            method: 'post',
            url: 'campaigns/update-workflow',
            data: {
              workflowId: this.value.workflow_id,
              features: {
                voucher_codes: !!this.model.voucher_codes_enabled,
                new_mxa: !!this.model.new_mxa
              }
            }
          })
        )
          .then(() => {
            this.workflowLoading = false
          })
          .catch(error => {
            this.workflowLoading = false
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      }
    }
  },

  mounted() {
    EventBus.$on('nav-state-change', navState => {
      this.isCollapsed = navState === 'collapsed'
    })

    if (this.workflowId && this.canLiveEditJourney) {
      this.fetchWorkflowMeta()
    }
  },

  computed: {
    url() {
      return this.model.new_mxa
        ? `https://${this.mxaDomain}/project/${this.projectId}/workflow/${this.workflowId}/preview`
        : `https://${this.mxaDomain}/old/project/${this.projectId}/workflow/${this.workflowId}/preview`
    },

    nextBtnLabel() {
      if (this.model.trigger === campaignTriggerTypes.TRIGGER_LOCATION) {
        return this.$t('CampaignForm_Content_NextButton_Title_Locations')
      }

      if (this.model.trigger === campaignTriggerTypes.TRIGGER_EVENT) {
        return this.$t('CampaignForm_Content_NextButton_Title_Events')
      }

      return this.$t('CampaignForm_Content_NextButton_Title_Segment')
    },

    readonly() {
      return this.$route.name === 'ViewCampaign'
    },

    workflowVariants() {
      return this.model.variants
        .filter(
          v =>
            v.key !== this.variant &&
            this.model.messages[12].hasOwnProperty(v.key) &&
            this.model.messages[12][v.key].content.workflow_id
        )
        .map(v => {
          return {
            title:
              `${this.$t('MessageType_Workflow')} / ${this.$t(
                'Message_Variant'
              )}  ` + v.key,
            workflow_id: this.model.messages[12][v.key].content.workflow_id
          }
        })
    },
    hasVoucherCodes() {
      return (
        this.model.trigger === campaignTriggerTypes.TRIGGER_NONE &&
        ($hasFeature('allowVoucherCodes') || this.model.voucher_codes_enabled)
      )
    },
    isFramed() {
      return window.top !== window.self
    },
    canLiveEditJourney() {
      if (!$hasFeature('liveEditJourneys')) {
        return false
      }

      // JB2.0 only feature
      if (!this.model.new_mxa) {
        return false
      }

      // Draft campaigns/templates should not be able to edit workflow drafts
      if (
        this.model.status === undefined ||
        this.model.status === campaignStatus.NONE
      ) {
        return false
      }

      // Past campaigns should not be able to edit drafts
      if (this.model.status === campaignStatus.STOPPED) {
        return false
      }

      return true
    },
    lastEditedName() {
      const meta = this.workflowMeta
      return meta
        ? meta.lastModifiedByName ||
            meta.lastModifiedBy ||
            meta.createdByName ||
            meta.createdBy
        : ''
    },
    shouldShowEditButton() {
      return (
        this.workflowId &&
        $can('updateProjectItem') &&
        this.model.status !== campaignStatus.STOPPED &&
        !this.canLiveEditJourney &&
        !this.shouldShowDraftEditTopRight
      )
    },
    isDraftCampaign() {
      return (
        this.model.status === undefined ||
        this.model.status === campaignStatus.NONE
      )
    },
    shouldShowDraftEditTopRight() {
      return (
        $hasFeature('liveEditJourneys') &&
        !!this.model.new_mxa &&
        this.isDraftCampaign &&
        this.workflowId &&
        $can('updateProjectItem')
      )
    },
    liveEditFlagOn() {
      return $hasFeature('liveEditJourneys')
    },
    hasCopyFromVariants() {
      return this.model.ab && this.model.variants.length > 1
    },
    showLiveInfo() {
      return this.canLiveEditJourney && !this.showBuilder && !!this.workflowMeta
    },
    showCopyFromButton() {
      return (
        this.liveEditFlagOn &&
        !!this.model.new_mxa &&
        this.hasCopyFromVariants &&
        !this.showBuilder
      )
    },
    showTopBar() {
      return (
        this.showLiveInfo ||
        this.shouldShowDraftEditTopRight ||
        this.showCopyFromButton
      )
    },
    editDraftJourneyText() {
      return this.workflowMeta.hasDraft
        ? this.$t('CampaignForm_Workflow_EditDraftJourney')
        : this.$t('CampaignForm_Workflow_CreateDraftJourney')
    }
  },

  methods: {
    closeModal() {
      this.modalVisible = false
    },

    openCopyModal() {
      if (this.workflowVariants.length === 0) {
        this.modalVisible = true
      } else {
        this.modalIsLoading = true
        RestApi(this.getWorkflowStatsEndpoint())
          .then(({ data }) => {
            this.workflowsAvailableForCopy = this.workflowVariants.filter(v =>
              (data || []).includes(v.workflow_id)
            )
            this.modalVisible = true
            this.modalIsLoading = false
          })
          .catch(error => {
            this.workflowsAvailableForCopy = []
            this.modalVisible = true
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.modalIsLoading = false
          })
      }
    },

    applyCopy() {
      this.closeModal()
      this.isLoading = true
      RestApi(this.copyWorkflowEndpoint(this.selectedVariant.workflow_id))
        .then(({ data }) => {
          const messages = cloneDeep(this.model.messages)

          messages[12][this.variant].content.workflow_id = data.response.id

          this.$set(this.model, 'messages', messages)
          this.workflowId = data.response.id
          this.isLoading = false
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
          this.isLoading = false
        })
    },

    copyWorkflowEndpoint(workflowId) {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/copy-workflow-for-variant`,
        data: {
          workflow_id: workflowId
        }
      })
    },

    getWorkflowStatsEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `campaigns/check-workflows`,
        data: {
          workflow_ids: this.workflowVariants.map(v => v.workflow_id)
        }
      })
    },

    nextStep() {
      EventBus.$emit('campaign-create-next-step')
    },

    onClose() {
      this.showBuilder = false
      this.isDraft = false
      if (this.workflowId && this.canLiveEditJourney) {
        this.fetchWorkflowMeta()
      }
    },

    fetchWorkflowMeta() {
      RestApi(
        projectEndpoint({
          method: 'post',
          url: 'campaigns/get-workflow-meta',
          data: { workflowId: this.workflowId }
        })
      )
        .then(({ data }) => {
          if (data && data.id) {
            this.workflowMeta = data
            this.hasDraft = !!data.hasDraft
          }
        })
        .catch(() => {})
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString([], {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    handleEditDraft() {
      this.isDraft = true
      this.showBuilder = true
    },

    handleEdit() {
      if (
        this.model.status === campaignStatus.RUNNING ||
        this.model.status === campaignStatus.CLOSED
      ) {
        customConfirm(
          this.$t('Campaign_Dialog_WorkflowEdit_Title'),
          this.$t('Campaign_Dialog_WorkflowEdit_Message'),
          this.$t('Campaign_Dialog_Button_Continue'),
          this.$t('Campaign_Dialog_Button_Cancel')
        ).then(() => {
          this.isDisabled = true
          RestApi(
            projectEndpoint({
              method: 'post',
              url: `campaigns/${this.model.id}/pause`
            })
          )
            .then(({ data }) => {
              this.model.status = data.statusCode
              this.isDisabled = false
              this.showBuilder = true
            })
            .catch(error => {
              this.$message({
                message: error.message,
                type: 'error'
              })
              this.isDisabled = false
            })
        })
        return
      }

      this.showBuilder = true
    }
  }
}
</script>

<style scoped>
button {
  margin-top: 20px;
}

.workflow-top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  margin-top: -10px;
  margin-bottom: 8px;
}

.live-info {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.workflow-top-actions {
  display: inline-flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
}

.workflow-top-actions >>> .el-button {
  margin-top: 0;
  margin-left: 0;
}

.workflow-top-actions >>> .copy-from-text-btn {
  font-size: 14px;
  font-weight: 600;
  color: #1d87c8;
}

.workflow-top-actions >>> .copy-from-text-btn:hover,
.workflow-top-actions >>> .copy-from-text-btn:focus {
  color: #1a7ab4;
}

.live-badge {
  background: #10b981;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.last-edited {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #111827;
  vertical-align: middle;
}

.last-edited-avatar {
  border-radius: 50%;
  flex-shrink: 0;
}

.last-edited-name {
  color: #111827;
}

iframe {
  width: 100%;
  height: 700px;
  border: 1px solid #d6d8db;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
  /** 85 and 325 **/
}

.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}

/*noinspection CssUnusedSymbol*/
.nav-framed {
  margin-left: 20px;
}

/*noinspection CssUnusedSymbol*/
.nav-collapsed {
  margin-left: 85px;
}

/*noinspection CssUnusedSymbol*/
.nav-expanded {
  margin-left: 325px;
}

.btn-ct {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.Workflow--Actions {
  padding: 0 8px 10px;
}

.Workflow--Actions button {
  margin-top: 0;
}

.table-container.outlined {
  border: 1px solid #dbdee7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/Workflow.vue